// eslint-disable-next-line import/no-extraneous-dependencies
import type { SearchResponse } from "@algolia/client-search";
import { SELECTED_TICKER_SYMBOLS_ATOM } from "components/charts/modern/overlay/TickerSymbolSelect/atoms";
import Avatar from "components/core/Avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/Dialog";
import classNames from "components/ui/classNames";
import { AnimatePresence, motion } from "framer-motion";
import { useAtom } from "jotai";
import indexes from "lib/algolia/indexes";
import searchClient from "lib/algolia/searchClient";
import type { IIssuerObject } from "lib/algolia/types/assets";
import debounce from "lodash/debounce";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IPlatformVertical } from "types/graphqlTypes";

const ISSUER_TYPES_BY_VERTICAL: Record<IPlatformVertical, IIssuerObject["type"][]> = {
  [IPlatformVertical.Stocks]: ["AMERICAN_COMPANY", "AMERICAN_REIT"],
  [IPlatformVertical.Reits]: ["AMERICAN_REIT"],
  [IPlatformVertical.Acoes]: ["BRAZILIAN_COMPANY"],
  [IPlatformVertical.Fiis]: ["BRAZILIAN_REAL_ESTATE_FUND"],
  [IPlatformVertical.All]: ["AMERICAN_COMPANY", "AMERICAN_REIT", "BRAZILIAN_COMPANY", "BRAZILIAN_REAL_ESTATE_FUND"],
};

interface TickerSymbolSelectProps {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  placeholder?: string;
  noResultsText?: string;
  hitsPerPage?: number;
  debounceMs?: number;
  disabled?: boolean;
  issuerTypes?: IPlatformVertical[];
  selectionLimit?: number;
  excludeTickerSymbols?: string[];
  onSelect?: (hit: IIssuerObject, tickerSymbol: string) => void;
}

export default function TickerSymbolSelect({
  children,
  title = <FormattedMessage defaultMessage="Pesquise por ticker" id="+4tLaU" />,
  description = (
    <FormattedMessage
      defaultMessage="Utilize o ticker completo, por exemplo: <strong>AAPL</strong>, <strong>WEGE3</strong> ou <strong>HGLG11</strong>"
      id="Oip2gp"
      values={{
        // eslint-disable-next-line react/no-unstable-nested-components
        strong: (text) => <strong className="font-medium">{text}</strong>,
      }}
    />
  ),
  placeholder,
  noResultsText,
  hitsPerPage = 5,
  debounceMs = 200,
  disabled = false,
  issuerTypes,
  selectionLimit = 4,
  excludeTickerSymbols,
  onSelect,
}: TickerSymbolSelectProps) {
  const intl = useIntl();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTickerSymbols, setSelectedTickerSymbols] = useAtom(SELECTED_TICKER_SYMBOLS_ATOM);
  const hasReachedSelectionLimit = selectedTickerSymbols.length >= selectionLimit;
  const excludedTickerSymbols = useMemo(
    () => new Set((excludeTickerSymbols ?? []).map((tickerSymbol) => tickerSymbol.toUpperCase())),
    [excludeTickerSymbols],
  );
  const [hits, setHits] = useState<IIssuerObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const latestQueryRef = useRef(0);
  const facetFilters = useMemo(() => {
    if (!issuerTypes || issuerTypes.length === 0) {
      return undefined;
    }
    const types = Array.from(new Set(issuerTypes.flatMap((vertical) => ISSUER_TYPES_BY_VERTICAL[vertical] ?? [])));
    if (types.length === 0) {
      return undefined;
    }
    return [types.map((type) => `type:${type}`)];
  }, [issuerTypes]);
  const facetFiltersRef = useRef(facetFilters);
  facetFiltersRef.current = facetFilters;

  const performSearch = useCallback(
    debounce(async (query: string) => {
      const queryId = latestQueryRef.current + 1;
      latestQueryRef.current = queryId;

      if (query.trim() === "") {
        setHits([]);
        setLoading(false);
        setHasSearched(false);
        return;
      }

      setLoading(true);
      try {
        const { results } = await searchClient.search<IIssuerObject>([
          {
            indexName: indexes.issuers,
            query,
            params: {
              hitsPerPage,
              restrictSearchableAttributes: ["tickerSymbols"],
              facetFilters: facetFiltersRef.current,
              typoTolerance: false,
            },
          },
        ]);

        if (queryId !== latestQueryRef.current) {
          return;
        }

        const [firstResult] = results as SearchResponse<IIssuerObject>[];
        setHits(firstResult?.hits ?? []);
        setHasSearched(true);
      } finally {
        if (queryId === latestQueryRef.current) {
          setLoading(false);
        }
      }
    }, debounceMs),
    [hitsPerPage, debounceMs],
  );

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild={true}>{children}</DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 bg-white sm:rounded-3xl">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-sm italic tracking-wide text-slate-500">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative flex items-center">
            <input
              type="text"
              name="tickerSymbol"
              aria-label="Ticker"
              maxLength={6}
              onKeyDown={(event) => {
                // Only allow alphanumeric characters and dots
                if (event.key.length === 1 && !/^[a-zA-Z0-9.]$/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={(event) => performSearch(event.target.value)}
              spellCheck={false}
              autoComplete="off"
              placeholder={
                placeholder ||
                intl.formatMessage({
                  defaultMessage: "Digite um ticker...",
                  id: "cH1VKE",
                })
              }
              disabled={disabled || hasReachedSelectionLimit}
              className={classNames(
                "block w-full rounded-md border-0 px-3 py-3 pr-14 text-gray-900 shadow-sm",
                "bg-white ring-1 ring-inset ring-slate-300/60",
                "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-100 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-300",
                "font-mono text-sm",
                "font-bold uppercase tracking-widest text-slate-700",
                "focus:placeholder:font-normal focus:placeholder:normal-case focus:placeholder:tracking-normal",
                "placeholder:font-normal placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400",
                "focus:placeholder:text-slate-500",
                "disabled:bg-gray-50 disabled:opacity-50",
                "dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
                "dark:focus-visible:ring-slate-100/20 dark:focus-visible:ring-offset-transparent dark:focus-visible:placeholder:text-slate-400",
                "dark:focus-visible:text-white",
                "dark:focus-visible:ring-1",
              )}
            />

            <div className="absolute inset-y-0 right-0 flex space-x-2 py-2 pr-2">
              {loading ? (
                <span className="inline-flex items-center px-3">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" strokeWidth={3} />
                </span>
              ) : (
                <>
                  <AnimatePresence>
                    {hasSearched && hits.length === 0 && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={classNames(
                          "inline-flex items-center rounded-md border border-rose-100 bg-rose-50/50 px-1.5 text-xs font-bold text-rose-500",
                          "dark:border-transparent dark:bg-rose-950/10 dark:text-rose-300",
                        )}
                      >
                        {noResultsText || <FormattedMessage defaultMessage="Nada..." id="BD9XRS" />}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  <kbd
                    className={classNames(
                      "inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-xs font-bold text-slate-500",
                      "dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
                    )}
                  >
                    Esc
                  </kbd>
                </>
              )}
            </div>
          </div>

          <div className="-mx-2 flex flex-col items-start empty:hidden">
            <AnimatePresence initial={false}>
              {hits.flatMap((hit) =>
                hit.tickerSymbols
                  .filter((tickerSymbol) => !excludedTickerSymbols.has(tickerSymbol.toUpperCase()))
                  .map((tickerSymbol) => {
                    const isSelected = selectedTickerSymbols.some((selected) => selected.tickerSymbol === tickerSymbol);
                    return (
                      <motion.button
                        type="button"
                        key={`${hit.objectID}/${tickerSymbol}`}
                        initial={{ opacity: 0, y: 0 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 0 }}
                        disabled={!isSelected && hasReachedSelectionLimit}
                        className={classNames(
                          "group my-1.5 flex w-full flex-1 items-center space-x-3 rounded-lg px-2 py-1",
                          "ring-1 ring-transparent ring-offset-2 ring-offset-transparent focus-visible:outline-none",
                          "focus-visible:bg-slate-50 focus-visible:ring-slate-200 focus-visible:ring-offset-white",
                          "disabled:cursor-not-allowed disabled:opacity-50",
                          {
                            "bg-violet-50 ring-1 ring-violet-200 ring-offset-2 ring-offset-white": isSelected,
                            "focus-visible:bg-violet-50 focus-visible:ring-violet-200 focus-visible:ring-offset-white":
                              isSelected,
                          },
                        )}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedTickerSymbols(
                              selectedTickerSymbols.filter((selected) => selected.tickerSymbol !== tickerSymbol),
                            );
                          } else if (!hasReachedSelectionLimit) {
                            setSelectedTickerSymbols([...selectedTickerSymbols, { issuer: hit, tickerSymbol }]);
                            onSelect?.(hit, tickerSymbol);
                          }
                        }}
                      >
                        <Avatar src={hit.logoUrl} hashValue={hit._id} alt={hit.name} size={42} squircle={true} />

                        <div className="w-full py-0.5 text-left">
                          <strong className="text-sm font-bold tracking-tight text-slate-800 dark:text-slate-300">
                            {hit.name}
                          </strong>
                          <span className="flex truncate text-xs font-medium italic tracking-wide text-slate-500">
                            {hit.type === "BRAZILIAN_REAL_ESTATE_FUND" ? hit.industrySegment : hit.industrySector}
                          </span>
                        </div>

                        <div className="ml-auto w-full space-x-3 text-right">
                          <span
                            className={classNames(
                              "transition-none duration-100 ease-in-out",
                              "rounded-xl px-2 py-1 text-xs tracking-wider dark:bg-gray-700 dark:text-gray-300",
                              "bg-violet-50 font-mono font-semibold text-violet-600 group-hover:bg-violet-600/30 group-hover:text-white",
                              "group-focus-visible:bg-slate-500 group-focus-visible:text-white",
                            )}
                          >
                            {tickerSymbol}
                          </span>
                        </div>
                      </motion.button>
                    );
                  }),
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
